import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import {
  Loader2,
  Plus,
  X,
  TrendingUp,
  Search,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Trophy,
  BarChart3,
  Target,
  FileText,
  Info,
  Sparkles,
  AlertTriangle,
  ArrowRight,
  Layers,
  Settings2
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { authFetch, handleResponse } from '@/utils/auth-fetch';
import { ComparisonResults } from './ComparisonResults';
import { SectionSelector } from './SectionSelector';
import { CompetitorDiscovery } from './CompetitorDiscovery';
import { useSectionSelection } from '@/hooks/useSectionSelection';

interface CompetitiveBenchmarkProps {
  sourceType: 'product' | 'category';
  sourceId: string;
  sourceName?: string;
}

export interface CompetitiveBenchmarkHandle {
  addCompetitorUrl: (url: string) => void;
}

interface Comparison {
  id: string;
  source_type: string;
  source_id: string;
  serp_query: string;
  serp_country: string;
  created_at: string;
  pages_analyzed: number;
  winner_title: string;
  winner_score: number;
  final_verdict: string;
}

interface ProductReadiness {
  success: boolean;
  productId: string;
  productName: string;
  category: string;
  siteId: string;
  status: 'ready' | 'needs_enrichment_ready' | 'needs_enrichment_partial' | 'needs_category_enrichment';
  hasAeoContent: boolean;
  hasEnrichment: boolean;
  lastEnriched: string | null;
  message: string;
  guidance: {
    level: 'high' | 'medium' | 'low';
    score: number;
    message: string;
    recommendation: string;
    estimatedTime?: string;
    canEnrichInline?: boolean;
    alternativeAction?: {
      label: string;
      path: string;
      description: string;
    };
    blockers?: string[];
    requiredActions?: string[];
    action?: {
      label: string;
      path: string;
      description: string;
    };
  } | null;
  action: string;
  categoryReadiness?: {
    overallScore: number;
    status: string;
    dimensions: Record<string, unknown>;
  };
}

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'UK', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
];

export const CompetitiveBenchmark = forwardRef<CompetitiveBenchmarkHandle, CompetitiveBenchmarkProps>(
  ({ sourceType, sourceId, sourceName }, ref) => {
  const { selectedSite } = useSite();
  const [activeTab, setActiveTab] = useState('new-analysis');

  // AEO Section Selection state
  const {
    mode: aeoMode,
    setMode: setAeoMode,
    preset,
    setPreset,
    selectedSections,
    setSelectedSections,
    getAeoSectionConfig,
    isLoading: loadingSectionConfig,
  } = useSectionSelection();

  // Competitor selection mode
  const [discoveryMode, setDiscoveryMode] = useState<'manual' | 'discover'>('manual');

  // New analysis state
  const [competitorUrls, setCompetitorUrls] = useState<string[]>(['']);
  const [manualContent, setManualContent] = useState<Array<{url: string, html: string}>>([]);
  const [showManualInput, setShowManualInput] = useState(false);
  const [currentManualUrl, setCurrentManualUrl] = useState('');
  const [currentManualHtml, setCurrentManualHtml] = useState('');

  // Expose addCompetitorUrl method via ref
  useImperativeHandle(ref, () => ({
    addCompetitorUrl: (url: string) => {
      setCompetitorUrls(current => {
        // Check if URL already exists
        if (current.includes(url)) {
          return current;
        }
        // Find first empty slot or add to end
        const emptyIndex = current.findIndex(u => u.trim() === '');
        if (emptyIndex !== -1) {
          const newUrls = [...current];
          newUrls[emptyIndex] = url;
          return newUrls;
        }
        // Add new URL if under limit
        if (current.length < 3) {
          return [...current, url];
        }
        return current;
      });
    },
  }));
  const [country, setCountry] = useState('US');
  const [searchQuery, setSearchQuery] = useState('');
  const [includeCurrentPage, setIncludeCurrentPage] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Results state
  const [currentComparisonId, setCurrentComparisonId] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);

  // Past comparisons state
  const [pastComparisons, setPastComparisons] = useState<Comparison[]>([]);
  const [loadingComparisons, setLoadingComparisons] = useState(false);

  // Readiness state
  const [readiness, setReadiness] = useState<ProductReadiness | null>(null);
  const [checkingReadiness, setCheckingReadiness] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [showEnrichDialog, setShowEnrichDialog] = useState(false);
  const [enrichmentProgress, setEnrichmentProgress] = useState(0);

  // Prepopulate search query from sourceName
  useEffect(() => {
    if (sourceName) {
      setSearchQuery(sourceName);
    }
  }, [sourceName]);

  // Load past comparisons
  useEffect(() => {
    if (selectedSite && activeTab === 'past-comparisons') {
      loadPastComparisons();
    }
  }, [selectedSite, activeTab]);

  const loadPastComparisons = async () => {
    if (!selectedSite) return;

    setLoadingComparisons(true);
    try {
      const response = await authFetch(
        `/api/aeo-benchmark/comparisons?siteId=${selectedSite.id}&sourceType=${sourceType}&limit=20`
      );
      const data = await handleResponse<{ comparisons: Comparison[] }>(response);
      setPastComparisons(data.comparisons);
    } catch (err) {
      console.error('Failed to load past comparisons:', err);
    } finally {
      setLoadingComparisons(false);
    }
  };

  // Check product readiness when component loads
  useEffect(() => {
    if (sourceType === 'product' && sourceId) {
      checkProductReadiness();
    }
  }, [sourceId, sourceType]);

  const checkProductReadiness = async () => {
    if (sourceType !== 'product' || !sourceId) return;

    setCheckingReadiness(true);
    try {
      const response = await authFetch(`/api/aeo-benchmark/product-readiness/${sourceId}`);
      const data = await handleResponse<ProductReadiness>(response);
      setReadiness(data);
    } catch (err) {
      console.error('Failed to check product readiness:', err);
      // Don't block the user, just assume ready if we can't check
      setReadiness(null);
    } finally {
      setCheckingReadiness(false);
    }
  };

  const triggerEnrichment = async () => {
    if (!sourceId) return;

    setEnriching(true);
    setEnrichmentProgress(0);

    // Simulate progress during enrichment
    const progressInterval = setInterval(() => {
      setEnrichmentProgress(prev => {
        if (prev >= 90) return prev;
        return prev + 10;
      });
    }, 2000);

    try {
      const response = await authFetch(`/api/aeo-benchmark/enrich-product/${sourceId}`, {
        method: 'POST',
        body: JSON.stringify({ waitForCompletion: true }),
      });

      await handleResponse(response);

      setEnrichmentProgress(100);
      clearInterval(progressInterval);

      // Refresh readiness check
      await checkProductReadiness();

      setShowEnrichDialog(false);
    } catch (err: any) {
      console.error('Enrichment failed:', err);
      setError(err.message || 'Failed to enrich product');
      clearInterval(progressInterval);
    } finally {
      setEnriching(false);
      setEnrichmentProgress(0);
    }
  };

  const addCompetitorUrl = () => {
    if (competitorUrls.length < 3) {
      setCompetitorUrls([...competitorUrls, '']);
    }
  };

  const removeCompetitorUrl = (index: number) => {
    setCompetitorUrls(competitorUrls.filter((_, i) => i !== index));
  };

  const updateCompetitorUrl = (index: number, value: string) => {
    const updated = [...competitorUrls];
    updated[index] = value;
    setCompetitorUrls(updated);
  };

  const addManualContent = () => {
    if (!currentManualUrl.trim() || !currentManualHtml.trim()) {
      setError('Please provide both URL and HTML content');
      return;
    }

    try {
      new URL(currentManualUrl);
    } catch {
      setError('Invalid URL format');
      return;
    }

    setManualContent([...manualContent, { url: currentManualUrl, html: currentManualHtml }]);
    setCurrentManualUrl('');
    setCurrentManualHtml('');
    setShowManualInput(false);
    setError(null);
  };

  const removeManualContent = (index: number) => {
    setManualContent(manualContent.filter((_, i) => i !== index));
  };

  const validateUrls = () => {
    const nonEmptyUrls = competitorUrls.filter(url => url.trim() !== '');
    if (nonEmptyUrls.length === 0) {
      setError('Please add at least one competitor URL or we will fetch from SERP automatically');
      return false;
    }

    // Basic URL validation
    for (const url of nonEmptyUrls) {
      try {
        new URL(url);
      } catch {
        setError(`Invalid URL: ${url}`);
        return false;
      }
    }

    return true;
  };

  const startAnalysis = async () => {
    setError(null);

    if (!selectedSite) {
      setError('No site selected');
      return;
    }

    // Get non-empty URLs
    const validUrls = competitorUrls.filter(url => url.trim() !== '');

    // If no URLs provided, we'll rely on SERP
    if (validUrls.length > 0 && !validateUrls()) {
      return;
    }

    setAnalyzing(true);

    try {
      const response = await authFetch('/api/aeo-benchmark/analyze', {
        method: 'POST',
        body: JSON.stringify({
          siteId: selectedSite.id,
          sourceType,
          sourceId,
          competitorUrls: validUrls,
          manualContent: manualContent,
          country,
          searchQuery: searchQuery || sourceName || `Best ${sourceType}`,
          includeCurrentPage,
          aeoSectionConfig: getAeoSectionConfig(),
        }),
      });

      const data = await handleResponse<{ comparisonId: string }>(response);

      console.log('Analysis started:', data.comparisonId);
      setCurrentComparisonId(data.comparisonId);
      setShowResults(true);
      setActiveTab('results');

    } catch (err: any) {
      console.error('Analysis failed:', err);
      setError(err.message || 'Failed to start analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const viewComparison = (comparisonId: string) => {
    setCurrentComparisonId(comparisonId);
    setShowResults(true);
    setActiveTab('results');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Competitive AEO Benchmark
          </CardTitle>
          <CardDescription>
            Compare your AEO-optimized {sourceType} page against competitors and SERP leaders
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="new-analysis">
                <Target className="h-4 w-4 mr-2" />
                New Analysis
              </TabsTrigger>
              <TabsTrigger value="results" disabled={!showResults}>
                <BarChart3 className="h-4 w-4 mr-2" />
                Results
              </TabsTrigger>
              <TabsTrigger value="past-comparisons">
                <TrendingUp className="h-4 w-4 mr-2" />
                Past Comparisons
              </TabsTrigger>
            </TabsList>

            <TabsContent value="new-analysis" className="space-y-6 mt-6">
              {/* Source Info */}
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Analyzing: <strong>{sourceName || sourceId}</strong>
                  <br />
                  We'll compare your AEO-optimized page against competitors and automatically fetch the top SERP result.
                </AlertDescription>
              </Alert>

              {/* Readiness Check Status */}
              {sourceType === 'product' && (
                <>
                  {checkingReadiness && (
                    <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <AlertDescription>
                        Checking product AEO readiness...
                      </AlertDescription>
                    </Alert>
                  )}

                  {readiness && readiness.status === 'ready' && (
                    <Alert className="border-green-200 bg-green-50 dark:bg-green-950/20">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <AlertTitle className="text-green-800 dark:text-green-400">
                        Product is AEO-Ready
                      </AlertTitle>
                      <AlertDescription className="text-green-700 dark:text-green-300">
                        This product has been enriched with AEO content and is ready for competitive benchmark.
                        {readiness.lastEnriched && (
                          <span className="block mt-1 text-xs">
                            Last enriched: {new Date(readiness.lastEnriched).toLocaleDateString()}
                          </span>
                        )}
                      </AlertDescription>
                    </Alert>
                  )}

                  {readiness && readiness.status === 'needs_enrichment_ready' && (
                    <Alert className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20">
                      <Sparkles className="h-4 w-4 text-yellow-600" />
                      <AlertTitle className="text-yellow-800 dark:text-yellow-400">
                        Product Needs AEO Enrichment
                      </AlertTitle>
                      <AlertDescription className="text-yellow-700 dark:text-yellow-300">
                        <p>{readiness.guidance?.message}</p>
                        <p className="mt-2">{readiness.guidance?.recommendation}</p>
                        <div className="mt-3 flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => setShowEnrichDialog(true)}
                            className="bg-yellow-600 hover:bg-yellow-700"
                          >
                            <Sparkles className="h-4 w-4 mr-2" />
                            Enrich Product Now
                          </Button>
                          <Badge variant="outline" className="self-center">
                            ~1-2 minutes
                          </Badge>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}

                  {readiness && readiness.status === 'needs_enrichment_partial' && (
                    <Alert className="border-orange-200 bg-orange-50 dark:bg-orange-950/20">
                      <AlertTriangle className="h-4 w-4 text-orange-600" />
                      <AlertTitle className="text-orange-800 dark:text-orange-400">
                        Limited Context Available (Score: {readiness.guidance?.score}%)
                      </AlertTitle>
                      <AlertDescription className="text-orange-700 dark:text-orange-300">
                        <p>{readiness.guidance?.message}</p>
                        <p className="mt-2">{readiness.guidance?.recommendation}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowEnrichDialog(true)}
                            className="border-orange-400 text-orange-700"
                          >
                            <Sparkles className="h-4 w-4 mr-2" />
                            Enrich Anyway (Lower Quality)
                          </Button>
                          {readiness.guidance?.alternativeAction && (
                            <Button
                              size="sm"
                              onClick={() => window.location.href = readiness.guidance?.alternativeAction?.path || '#'}
                            >
                              <ArrowRight className="h-4 w-4 mr-2" />
                              {readiness.guidance.alternativeAction.label}
                            </Button>
                          )}
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}

                  {readiness && readiness.status === 'needs_category_enrichment' && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>Category Not Ready for AEO</AlertTitle>
                      <AlertDescription>
                        <p>{readiness.guidance?.message}</p>
                        <p className="mt-2 font-medium">{readiness.guidance?.recommendation}</p>
                        {readiness.guidance?.blockers && readiness.guidance.blockers.length > 0 && (
                          <ul className="mt-2 list-disc list-inside text-sm">
                            {readiness.guidance.blockers.slice(0, 3).map((blocker, i) => (
                              <li key={i}>{blocker}</li>
                            ))}
                          </ul>
                        )}
                        <div className="mt-4">
                          {readiness.guidance?.action && (
                            <Button
                              onClick={() => window.location.href = readiness.guidance?.action?.path || '#'}
                            >
                              <ArrowRight className="h-4 w-4 mr-2" />
                              {readiness.guidance.action.label}
                            </Button>
                          )}
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}

              {/* Search Query */}
              <div className="space-y-2">
                <Label>Search Query (for SERP lookup)</Label>
                <Input
                  placeholder={`e.g., Best ${sourceName || sourceType}`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <p className="text-sm text-muted-foreground">
                  Used to fetch the top-ranked competitor from search engines
                </p>
              </div>

              {/* Country Selection */}
              <div className="space-y-2">
                <Label>Target Market</Label>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Include Current Product Page */}
              {sourceType === 'product' && (
                <div className="flex items-center space-x-2 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-900">
                  <Checkbox
                    id="include-current"
                    checked={includeCurrentPage}
                    onCheckedChange={(checked) => setIncludeCurrentPage(checked as boolean)}
                  />
                  <div className="flex-1">
                    <Label
                      htmlFor="include-current"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      Include current product page in comparison
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      When available, compare your AEO page against the existing product page on your site
                    </p>
                  </div>
                </div>
              )}

              {/* AEO Mode Selection */}
              {sourceType === 'product' && (
                <Card className="border-2 border-dashed">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Layers className="h-4 w-4 text-blue-500" />
                        AEO Benchmark Mode
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-sm ${aeoMode === 'full' ? 'text-muted-foreground' : 'font-medium'}`}>
                          Partial
                        </span>
                        <Switch
                          checked={aeoMode === 'full'}
                          onCheckedChange={(checked) => setAeoMode(checked ? 'full' : 'partial')}
                          disabled={loadingSectionConfig || analyzing}
                        />
                        <span className={`text-sm ${aeoMode === 'full' ? 'font-medium' : 'text-muted-foreground'}`}>
                          Full AEO
                        </span>
                      </div>
                    </CardTitle>
                    <CardDescription>
                      {aeoMode === 'full' ? (
                        'Compare against a fully AEO-optimized page with all sections'
                      ) : (
                        'Select specific AEO sections to test their incremental impact'
                      )}
                    </CardDescription>
                  </CardHeader>

                  {aeoMode === 'partial' && (
                    <CardContent className="pt-0">
                      <SectionSelector
                        selectedSections={selectedSections}
                        onSectionsChange={setSelectedSections}
                        selectedPreset={preset}
                        onPresetChange={setPreset}
                        disabled={analyzing || loadingSectionConfig}
                      />
                    </CardContent>
                  )}
                </Card>
              )}

              {/* Competitor Selection Mode Toggle */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Label>Competitor Selection:</Label>
                  <div className="flex items-center gap-3 p-1 rounded-lg bg-muted">
                    <Button
                      type="button"
                      variant={discoveryMode === 'manual' ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setDiscoveryMode('manual')}
                      disabled={analyzing}
                    >
                      Manual URLs
                    </Button>
                    <Button
                      type="button"
                      variant={discoveryMode === 'discover' ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setDiscoveryMode('discover')}
                      disabled={analyzing}
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      AI Discovery
                    </Button>
                  </div>
                </div>

                {discoveryMode === 'discover' ? (
                  <CompetitorDiscovery
                    productName={sourceName || ''}
                    productCategory={readiness?.category || ''}
                    productId={sourceId}
                    country={country}
                    ownDomain={selectedSite?.domain || selectedSite?.url}
                    onCompetitorsSelected={(urls) => setCompetitorUrls(urls.length > 0 ? urls : [''])}
                    disabled={analyzing}
                  />
                ) : (
                  <>
                    {/* Manual Competitor URLs */}
                    <div className="flex items-center justify-between">
                      <Label>Competitor URLs (optional, 0-3 URLs)</Label>
                      {competitorUrls.length < 3 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addCompetitorUrl}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add URL
                        </Button>
                      )}
                    </div>

                    {competitorUrls.map((url, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          placeholder="https://competitor.com/product-page"
                          value={url}
                          onChange={(e) => updateCompetitorUrl(index, e.target.value)}
                        />
                        {competitorUrls.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeCompetitorUrl(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}

                    <p className="text-sm text-muted-foreground">
                      Leave empty to only compare against SERP results
                    </p>
                  </>
                )}
              </div>

              {/* Manual Content Submission */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Manual Content (for blocked sites)</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      If a site blocks automated access, open it in your browser, view source, and paste the HTML here
                    </p>
                  </div>
                  {!showManualInput && manualContent.length < 3 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowManualInput(true)}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Add Manual
                    </Button>
                  )}
                </div>

                {/* Show added manual content */}
                {manualContent.length > 0 && (
                  <div className="space-y-2">
                    {manualContent.map((item, index) => (
                      <div key={index} className="flex items-center gap-2 p-3 border rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.url}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.html.length.toLocaleString()} characters
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeManualContent(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Manual input form */}
                {showManualInput && (
                  <Card className="border-2 border-blue-200 dark:border-blue-800">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Info className="h-4 w-4" />
                        How to get HTML source
                      </CardTitle>
                      <CardDescription className="text-xs space-y-1">
                        <p>1. Open the competitor page in your browser</p>
                        <p>2. Right-click anywhere and select "View Page Source" (or press Ctrl+U / Cmd+Option+U)</p>
                        <p>3. Select all (Ctrl+A / Cmd+A) and copy (Ctrl+C / Cmd+C)</p>
                        <p>4. Paste the HTML below</p>
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <Label>Page URL</Label>
                        <Input
                          placeholder="https://example.com/product"
                          value={currentManualUrl}
                          onChange={(e) => setCurrentManualUrl(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>HTML Source (paste here)</Label>
                        <Textarea
                          placeholder="Paste the complete HTML source here..."
                          value={currentManualHtml}
                          onChange={(e) => setCurrentManualHtml(e.target.value)}
                          rows={10}
                          className="font-mono text-xs"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {currentManualHtml.length > 0
                            ? `${currentManualHtml.length.toLocaleString()} characters`
                            : 'Paste the HTML source from your browser'}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          onClick={addManualContent}
                          disabled={!currentManualUrl.trim() || !currentManualHtml.trim()}
                        >
                          Add Manual Content
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            setShowManualInput(false);
                            setCurrentManualUrl('');
                            setCurrentManualHtml('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Error Display */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Action Button */}
              <Button
                onClick={startAnalysis}
                disabled={
                  analyzing ||
                  !selectedSite ||
                  checkingReadiness ||
                  (readiness && readiness.status === 'needs_category_enrichment') ||
                  (readiness && !readiness.hasAeoContent && readiness.status !== 'ready')
                }
                className="w-full"
                size="lg"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing... (30-60 seconds)
                  </>
                ) : checkingReadiness ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Checking product readiness...
                  </>
                ) : readiness && readiness.status === 'needs_category_enrichment' ? (
                  <>
                    <AlertCircle className="h-4 w-4 mr-2" />
                    Category enrichment required first
                  </>
                ) : readiness && !readiness.hasAeoContent && readiness.status !== 'ready' ? (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Enrich product first (see above)
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Start Competitive Analysis
                  </>
                )}
              </Button>

              {analyzing && (
                <Alert>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <AlertDescription>
                    Analyzing pages with AI... This takes 30-60 seconds.
                    <br />
                    • Generating your AEO page
                    <br />
                    • Fetching competitor pages
                    <br />
                    • Running AI evaluation
                    <br />
                    • Computing rankings
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>

            <TabsContent value="results" className="mt-6">
              {showResults && currentComparisonId ? (
                <ComparisonResults
                  comparisonId={currentComparisonId}
                  onRerunWithManualContent={(newManualContent) => {
                    setManualContent(newManualContent);
                    // Trigger a new analysis with the manual content
                    startAnalysis();
                  }}
                />
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No results to display. Start a new analysis to see results.
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>

            <TabsContent value="past-comparisons" className="mt-6">
              {loadingComparisons ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : pastComparisons.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No past comparisons found. Run your first analysis to get started.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-4">
                  {pastComparisons.map((comparison) => (
                    <Card key={comparison.id} className="cursor-pointer hover:bg-accent"
                          onClick={() => viewComparison(comparison.id)}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                              {comparison.winner_title}
                              <Badge variant="secondary">
                                {comparison.winner_score}/100
                              </Badge>
                            </CardTitle>
                            <CardDescription className="mt-1">
                              Query: {comparison.serp_query} • {comparison.serp_country}
                            </CardDescription>
                          </div>
                          <Button variant="ghost" size="sm">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <CheckCircle2 className="h-4 w-4" />
                            {comparison.pages_analyzed} pages analyzed
                          </span>
                          <span>
                            {new Date(comparison.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        {comparison.final_verdict && (
                          <p className="mt-2 text-sm line-clamp-2">
                            {comparison.final_verdict}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Enrichment Confirmation Dialog */}
      <Dialog open={showEnrichDialog} onOpenChange={setShowEnrichDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-yellow-500" />
              Enrich Product for AEO
            </DialogTitle>
            <DialogDescription>
              {readiness?.guidance?.level === 'medium' ? (
                <div className="space-y-2 mt-2">
                  <p className="text-orange-600 font-medium">
                    Warning: Limited category context available
                  </p>
                  <p>
                    The category readiness score is {readiness.guidance?.score}%. Results may have lower quality.
                    For best results, consider enriching more products in this category first.
                  </p>
                </div>
              ) : (
                <p className="mt-2">
                  This will generate AEO-optimized content for <strong>{readiness?.productName}</strong>,
                  including enriched attributes and FAQ content.
                </p>
              )}
            </DialogDescription>
          </DialogHeader>

          {enriching ? (
            <div className="py-6 space-y-4">
              <div className="flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-yellow-500" />
              </div>
              <div className="space-y-2">
                <Progress value={enrichmentProgress} className="w-full" />
                <p className="text-sm text-center text-muted-foreground">
                  {enrichmentProgress < 50
                    ? 'Extracting product attributes...'
                    : enrichmentProgress < 90
                    ? 'Generating AEO content...'
                    : 'Finalizing...'}
                </p>
              </div>
              <p className="text-xs text-center text-muted-foreground">
                This typically takes 1-2 minutes. Please wait...
              </p>
            </div>
          ) : (
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowEnrichDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={triggerEnrichment}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Start Enrichment
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
});

CompetitiveBenchmark.displayName = 'CompetitiveBenchmark';
